<template>
  <modal :open="internalOpen" :width="900">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ modalTitle }}</h1>
      </modal-header>
    </template>

    <form @submit.prevent="save">
      <div class="mt-8 flex flex-col gap-y-5">
        <!-- Warning for existing payouts with app payment -->
        <div v-if="props.payment?.showInApp" class="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-800">
          <p class="font-medium">{{ $t('pages.additionalPayments.editDisabled.title') }}</p>
          <p class="mt-1">{{ $t('pages.additionalPayments.editDisabled.description') }}</p>
        </div>

        <Multiselect
          v-model="form.driver"
          :options="driverOptions"
          :label="$t('campaignRegistration.driver')"
          :can-deselect="false"
          :can-clear="false"
          :searchable="true"
          :minChars="2"
          :filterResults="false"
          :delay="300"
          :resolveOnLoad="true"
          :createOption="false"
          :clearOnSelect="true"
          :noOptionsText="$t('inputSearchString')"
        />

        <Multiselect
          v-model="form.reason"
          :options="reasonOptions"
          :label="$t('pages.additionalPayments.reason')"
          :can-deselect="false"
          :can-clear="false"
          :searchable="true"
        />

        <Multiselect
          v-if="isCampaignReason"
          :key="campaignSelectKey"
          v-model="form.campaign"
          :options="campaignOptions"
          :label="$t('campaign.DEFAULT')"
          :disabled="!form.driver || campaignsLoading || !campaignsInitialLoaded"
          :can-deselect="false"
          :can-clear="false"
          :searchable="true"
          :resolveOnLoad="true"
          :minChars="0"
          :filterResults="false"
          :delay="200"
          :clearOnSearch="false"
          :noOptionsText="$t('inputSearchString')"
        />

        <Input
          v-model="form.amountChf"
          type="number"
          step="0.01"
          min="0.01"
          :disabled="isCampaignReason && !form.campaign"
          :label="$t('pages.additionalPayments.amountChf')"
        />

        <Multiselect
          v-model="form.status"
          :options="statusOptions"
          :label="$t('pages.additionalPayments.status')"
          :can-deselect="false"
          :can-clear="false"
          :searchable="false"
        />

        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-10"
            :busy="loading"
            :disabled="!isFormValid || (isEditMode && props.payment?.showInApp)"
            @click="save"
          >
            {{ isEditMode && props.payment?.showInApp ? $t('pages.additionalPayments.editDisabled.cannotSave') : $t('save') }}
          </custom-button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Modal from '@utils/Modal/Modal.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import Multiselect from '@utils/Multiselect.vue';
import Input from '@utils/inputs/Input.vue';
import CustomButton from '@utils/CustomButton.vue';
import { enumOptionsProvider, collOptionsProvider, type ProviderParams } from '@utils/table/hooks/provider';
import { fetchApi, useFetchAction } from '@hooks/fetch';
import { useNotify } from '@utils/notifications';
import type { PayoutAdditional, Driver } from '@/entities/Entities';

interface Props {
  open: boolean;
  variant?: 'general' | 'campaign';
  payment?: PayoutAdditional;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:open', 'saved']);
const internalOpen = useVModel(props, 'open', emit);
const { t, te } = useI18n();
const notify = useNotify();

const [exec, { loading }] = useFetchAction();
const campaignsInitialLoaded = ref(false);
const campaignsLoading = ref(false);
const campaignSelectKey = ref(0);
let campaignLoadSeq = 0;
const suppressDriverReset = ref(false);

const isEditMode = computed(() => !!props.payment?.id);

// Campaign reason check - show campaign fields when CampaignTopUp is selected
const isCampaignReason = computed(() => {
  if (isEditMode.value) {
    return props.payment?.reason === 'CampaignTopUp' || props.payment?.paymentType === 'campaign';
  }
  return form.value.reason === 'CampaignTopUp';
});

const form = ref({
  driver: undefined as string | undefined,
  campaign: undefined as string | undefined,
  reason: undefined as string | undefined,
  amountChf: undefined as number | undefined,
  status: 'open' as string,
});

interface DriverCampaignOption {
  id: string;
  name: string;
}

async function loadCampaignOptions(query: string, driverOverride?: string) {
  if (!isCampaignReason.value) return [];
  const driver = driverOverride ?? form.value.driver;
  if (!driver) return [];
  const queryParams: string[][] = [['driver', driver]];
  if (query) queryParams.push(['q', query]);

  const campaigns = await fetchApi<DriverCampaignOption[]>('/payout_additional_campaign/driver-campaign-options', {
    query: queryParams,
  });

  return campaigns.map((value) => ({
    label: value.name,
    value: value.id,
  }));
}

const campaignOptions = (query: string) => loadCampaignOptions(query);

async function preloadCampaignOptionsForDriver(driverIri: string, currentSeq: number) {
  campaignsLoading.value = true;
  try {
    await loadCampaignOptions('', driverIri);
    if (currentSeq !== campaignLoadSeq || form.value.driver !== driverIri) return;
    await nextTick();
    campaignsInitialLoaded.value = true;
  } catch {
    if (currentSeq !== campaignLoadSeq || form.value.driver !== driverIri) return;
    campaignsInitialLoaded.value = false;
    notify?.({ variant: 'error', title: t('pages.additionalPayments.form.saveError') });
  } finally {
    if (currentSeq === campaignLoadSeq) campaignsLoading.value = false;
  }
}

const driverOptions = (query: string, select: any) => {
  const input = query || select.modelValue;
  if (!input) return Promise.resolve([]);
  const filterQuery: ProviderParams = {
    filter: query ? { 'user.fullName': input } : { id: select.modelValue },
  };
  return collOptionsProvider<Driver>('/drivers', filterQuery, (value) => ({
    label: `${value.user?.fullName ?? ''} (${value.user?.email ?? '-'})`,
    value: value['@id'],
  }))();
};

const reasonOptions = enumOptionsProvider('/enums/PayoutAdditionalReason', {}, (item) => {
  const key = `pages.additionalPayments.reasonValues.${item}`;
  return {
    label: te(key) ? t(key) : String(item),
    value: String(item),
  };
});
const statusOptions = enumOptionsProvider('/enums/AdditionalPayoutStatusEnum', {}, (item) => {
  const key = `pages.additionalPayments.statusValues.${item}`;
  return {
    label: te(key) ? t(key) : String(item),
    value: String(item),
  };
});

const modalTitle = computed(() =>
  isEditMode.value
    ? isCampaignReason.value
      ? t('pages.additionalPayments.editCampaignTitle')
      : t('pages.additionalPayments.editTitle')
    : isCampaignReason.value
      ? t('pages.additionalPayments.createCampaignTitle')
      : t('pages.additionalPayments.createTitle'));

const isFormValid = computed(() => {
  const amount = Number(form.value.amountChf);
  const hasCommon = !!form.value.driver && !!form.value.reason;
  if (!isCampaignReason.value) {
    return hasCommon && Number.isFinite(amount) && amount > 0;
  }

  return hasCommon && !!form.value.campaign && Number.isFinite(amount) && amount > 0;
});

watch(() => form.value.driver, () => {
  if (suppressDriverReset.value) return;
  if (isCampaignReason.value) {
    const currentSeq = ++campaignLoadSeq;
    form.value.campaign = undefined;
    form.value.amountChf = undefined;
    campaignsInitialLoaded.value = false;
    campaignSelectKey.value += 1;
    campaignsLoading.value = false;
    if (!form.value.driver) return;

    const selectedDriver = form.value.driver;
    void preloadCampaignOptionsForDriver(selectedDriver, currentSeq);
  }
});

watch(() => form.value.reason, () => {
  if (suppressDriverReset.value) return;
  const currentSeq = ++campaignLoadSeq;
  // Reset campaign-related fields when switching reason.
  form.value.campaign = undefined;
  campaignsInitialLoaded.value = false;
  campaignSelectKey.value += 1;
  campaignsLoading.value = false;

  if (!isCampaignReason.value) {
    form.value.campaign = undefined;
    return;
  }

  if (!form.value.driver) return;
  void preloadCampaignOptionsForDriver(form.value.driver, currentSeq);
});

watch(() => form.value.campaign, () => {
  if (suppressDriverReset.value) return;
  if (isCampaignReason.value && !form.value.campaign) {
    form.value.amountChf = undefined;
  }
});

watch(
  () => [internalOpen.value, props.payment],
  async ([open]) => {
    if (!open) return;
    if (!props.payment) {
      resetForm();
      return;
    }

    suppressDriverReset.value = true;
    form.value = {
      driver: props.payment.driverId ? `/drivers/${props.payment.driverId}` : undefined,
      campaign: props.payment.campaignId ?? undefined,
      reason: props.payment.reason,
      amountChf: props.payment.amountChf,
      status: props.payment.status ?? 'open',
    };

    if (isCampaignReason.value) {
      const currentSeq = ++campaignLoadSeq;
      campaignsInitialLoaded.value = false;
      campaignSelectKey.value += 1;
      campaignsLoading.value = false;
      if (form.value.driver) {
        await preloadCampaignOptionsForDriver(form.value.driver, currentSeq);
      }
    }

    await nextTick();
    suppressDriverReset.value = false;
  },
  { immediate: true },
);

function resetForm() {
  form.value = {
    driver: undefined,
    campaign: undefined,
    reason: undefined,
    amountChf: undefined,
    status: 'open',
  };
  campaignsInitialLoaded.value = false;
  campaignsLoading.value = false;
}

function closeModal() {
  resetForm();
  internalOpen.value = false;
}

async function save() {
  const amount = Number(form.value.amountChf);
  if (!form.value.driver || !form.value.reason) {
    notify?.({ variant: 'error', title: t('pages.additionalPayments.form.validationError') });
    return;
  }
  if (isCampaignReason.value && !form.value.campaign) {
    notify?.({ variant: 'error', title: t('pages.additionalPayments.form.validationError') });
    return;
  }
  if (!Number.isFinite(amount) || amount <= 0) {
    notify?.({ variant: 'error', title: t('pages.additionalPayments.form.validationError') });
    return;
  }

  try {
    const endpoint = isEditMode.value
      ? `/payout_additional/${props.payment?.id}`
      : '/payout_additional';
    await exec(endpoint, {
      method: isEditMode.value ? 'PUT' : 'POST',
      body: {
        driver: form.value.driver,
        campaign: isCampaignReason.value ? form.value.campaign : undefined,
        reason: form.value.reason,
        amountChf: amount,
        status: form.value.status,
      },
    });

    notify?.({
      variant: 'success',
      title: isEditMode.value
        ? t('pages.additionalPayments.form.updateSuccess')
        : t('pages.additionalPayments.form.saveSuccess'),
    });
    emit('saved');
    closeModal();
  } catch {
    notify?.({
      variant: 'error',
      title: isEditMode.value
        ? t('pages.additionalPayments.form.updateError')
        : t('pages.additionalPayments.form.saveError'),
    });
  }
}
</script>
